import {useEffect,useRef,useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {FiCopy,FiFileText,FiMail,FiPlus,FiRepeat,FiUsers,FiMessageSquare,FiX} from 'react-icons/fi';
import {Repository} from '../data/repository';
import {isValidEmail} from '../utils/validators';
import {Button,Input,SafeIcon,TextArea,cn} from './UI';

const QUICK_CREATE_KEY='invoiceflow:quick-create';
const QUICK_CREATE_ROUTE_KEY='invoiceflow:quick-create-route';
const REQUEST_SUBJECT='Information required to set up your billing account';

type QuickCreateAction='invoice' | 'client' | 'recurring';
type Feedback={type: 'success' | 'error';message: string} | null;

const splitEmails=(value?: string): string[]=> String(value || '')
  .split(/[;,]/)
  .map(email=> email.trim())
  .filter(Boolean);

const mergeEmails=(...values: Array<string | undefined>): string=> {
  const seen=new Set<string>();
  const merged: string[]=[];
  values.flatMap(splitEmails).forEach(email=> {
    const key=email.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(email);
    }
  });
  return merged.join(', ');
};

const buildBillingRequestBody=(): string=> {
  const business=Repository.settings.getBusiness();
  const automation=Repository.settings.getAutomation();
  const contactName=automation.accountsContactName || business.businessName || business.tradingName || 'BPA Solutions';
  const contactTitle=automation.accountsContactTitle || '';
  const contactEmail=automation.accountsContactEmail || automation.replyToEmail || business.email || '';
  const contactPhone=automation.accountsContactPhone || business.phone || '';
  const businessName=business.businessName || business.tradingName || '';
  const signature=[contactName,contactTitle,businessName && businessName!==contactName ? businessName : '',contactEmail,contactPhone].filter(Boolean).join('\n');

  return [
    'Hi,',
    '',
    'We are setting up your billing details in our accounts system.',
    '',
    'Could you please reply with the following information:',
    '',
    'Billing contact name',
    'Legal or business name',
    'ABN',
    'Primary accounts or billing email address',
    'Any additional email addresses that should receive invoices',
    'Contact phone number',
    'Street address',
    'Suburb',
    'State',
    'Postcode',
    'Country',
    '',
    'Once received, we will update our records for future invoices and account correspondence.',
    '',
    'Thank you.',
    '',
    'Kind regards,',
    signature
  ].filter((line,index,array)=> !(line==='' && index===array.length - 1)).join('\n');
};

const copyText=async (text: string): Promise<void>=> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea=document.createElement('textarea');
  textarea.value=text;
  textarea.style.position='fixed';
  textarea.style.opacity='0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied=document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Clipboard access is unavailable.');
};

export const QuickCreate=()=> {
  const navigate=useNavigate();
  const menuRef=useRef<HTMLDivElement | null>(null);
  const [isMenuOpen,setIsMenuOpen]=useState(false);
  const [isBillingRequestOpen,setIsBillingRequestOpen]=useState(false);
  const [feedback,setFeedback]=useState<Feedback>(null);
  const [request,setRequest]=useState({to: '',cc: '',bcc: '',subject: REQUEST_SUBJECT,body: ''});

  useEffect(()=> {
    if (!isMenuOpen) return;
    const closeOnEscape=(event: KeyboardEvent)=> {if (event.key==='Escape') setIsMenuOpen(false);};
    document.addEventListener('keydown',closeOnEscape);
    return ()=> document.removeEventListener('keydown',closeOnEscape);
  },[isMenuOpen]);

  useEffect(()=> {
    if (!isBillingRequestOpen) return;
    const closeOnEscape=(event: KeyboardEvent)=> {if (event.key==='Escape') setIsBillingRequestOpen(false);};
    document.addEventListener('keydown',closeOnEscape);
    return ()=> document.removeEventListener('keydown',closeOnEscape);
  },[isBillingRequestOpen]);

  const startQuickCreate=(action: QuickCreateAction,path: string)=> {
    sessionStorage.setItem(QUICK_CREATE_KEY,action);
    if (window.location.pathname !==path) {
      sessionStorage.setItem(QUICK_CREATE_ROUTE_KEY,path);
    } else {
      sessionStorage.removeItem(QUICK_CREATE_ROUTE_KEY);
    }
    setIsMenuOpen(false);
    navigate(path);
    window.setTimeout(()=> window.dispatchEvent(new CustomEvent('invoiceflow:quick-create')) ,60);
    window.setTimeout(()=> {
      if (sessionStorage.getItem(QUICK_CREATE_ROUTE_KEY)===path) {
        sessionStorage.removeItem(QUICK_CREATE_ROUTE_KEY);
      }
    },1500);
  };

  const startQuickSms=()=> {
    setIsMenuOpen(false);
    const target='/clients-products';
    // Keep the existing cross-route Quick Create loading behavior without
    // storing message content or creating any PocketBase records.
    if (window.location.hash.split('?')[0].slice(1)!==target) {
      sessionStorage.setItem(QUICK_CREATE_ROUTE_KEY,target);
    } else {
      sessionStorage.removeItem(QUICK_CREATE_ROUTE_KEY);
    }
    navigate(target+'?quickSms=1');
  };

  const openBillingRequest=()=> {
    const automation=Repository.settings.getAutomation();
    setRequest({
      to: '',
      cc: mergeEmails(automation.defaultCc,automation.permanentAuditCc),
      bcc: mergeEmails(automation.defaultBcc,automation.permanentAuditBcc),
      subject: REQUEST_SUBJECT,
      body: buildBillingRequestBody()
    });
    setFeedback(null);
    setIsMenuOpen(false);
    setIsBillingRequestOpen(true);
  };

  const validateRecipients=(): {to: string;cc: string[];bcc: string[]} | null=> {
    const to=request.to.trim();
    if (!to || !isValidEmail(to)) {
      setFeedback({type: 'error',message: 'Enter a valid recipient email address first.'});
      return null;
    }
    const cc=splitEmails(request.cc);
    const bcc=splitEmails(request.bcc);
    const invalid=[...cc,...bcc].find(email=> !isValidEmail(email));
    if (invalid) {
      setFeedback({type: 'error',message: `Check the CC/BCC address: ${invalid}`});
      return null;
    }
    return {to,cc,bcc};
  };

  const openEmail=()=> {
    const recipients=validateRecipients();
    if (!recipients) return;
    const params=new URLSearchParams();
    if (recipients.cc.length) params.set('cc',recipients.cc.join(','));
    if (recipients.bcc.length) params.set('bcc',recipients.bcc.join(','));
    params.set('subject',request.subject.trim() || REQUEST_SUBJECT);
    params.set('body',request.body);
    setFeedback({type: 'success',message: 'Opening your email app with the request prepared.'});
    window.location.href=`mailto:${encodeURIComponent(recipients.to)}?${params.toString()}`;
  };

  const copyRequest=async ()=> {
    const text=`Subject: ${request.subject.trim() || REQUEST_SUBJECT}\n\n${request.body}`;
    try {
      await copyText(text);
      setFeedback({type: 'success',message: 'Billing information request copied.'});
    } catch (error: any) {
      setFeedback({type: 'error',message: error?.message || 'Unable to copy the request.'});
    }
  };

  const menuItems=[
    {label: 'Quick SMS',description: 'Message a client or any mobile number',icon: FiMessageSquare,onClick: startQuickSms},
    {label: 'New Invoice',description: 'Open the existing invoice editor',icon: FiFileText,onClick: ()=> startQuickCreate('invoice','/invoices')},
    {label: 'New Client',description: 'Open the existing client form',icon: FiUsers,onClick: ()=> startQuickCreate('client','/clients-products')},
    {label: 'New Recurring Profile',description: 'Open the recurring billing setup',icon: FiRepeat,onClick: ()=> startQuickCreate('recurring','/payments-recurring')},
    {label: 'Request Billing Info',description: 'Prepare an email asking for billing details',icon: FiMail,onClick: openBillingRequest}
  ];

  return (
    <>
      <div ref={menuRef} className="relative shrink-0">
        <Button size="sm" onClick={()=> setIsMenuOpen(open=> !open)} aria-haspopup="menu" aria-expanded={isMenuOpen} className="px-2.5 sm:px-3">
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span className="hidden sm:inline">Create</span>
        </Button>

        {isMenuOpen && (
          <>
            <button type="button" aria-label="Close create menu" className="fixed inset-0 z-[10000] bg-slate-950/20 xl:bg-transparent" onClick={()=> setIsMenuOpen(false)} />
            <div role="menu" className="fixed left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+5.75rem)] z-[10010] flex max-h-[calc(100dvh_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom)_-_6.25rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl xl:absolute xl:left-auto xl:right-0 xl:top-[calc(100%+0.5rem)] xl:bottom-auto xl:w-80">
              <div className="shrink-0 border-b border-gray-100 px-4 py-3 xl:hidden">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-black text-gray-900">Quick Create</p>
                    <p className="mt-0.5 text-xs text-gray-500">Start a common InvoiceFlow task.</p>
                  </div>
                  <button type="button" onClick={()=> setIsMenuOpen(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Close quick create">
                    <SafeIcon icon={FiX} className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="min-h-0 overflow-y-auto overscroll-contain p-2">
                {menuItems.map(item=> (
                  <button key={item.label} type="button" role="menuitem" onClick={item.onClick} className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                      <SafeIcon icon={item.icon} className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-gray-900">{item.label}</span>
                      <span className="mt-0.5 block text-xs leading-snug text-gray-500">{item.description}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {isBillingRequestOpen && (
        <div className="fixed inset-0 z-[10050] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-4 sm:px-5">
              <div>
                <h3 className="text-lg font-black text-gray-900">Request Billing Information</h3>
                <p className="mt-1 text-xs leading-relaxed text-gray-500">Enter the prospective client's email address. The request and your configured CC/BCC defaults are prepared for you.</p>
              </div>
              <button type="button" onClick={()=> setIsBillingRequestOpen(false)} className="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Close billing information request">
                <SafeIcon icon={FiX} className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4 premium-scrollbar sm:p-5">
              <Input label="To" type="email" inputMode="email" autoComplete="email" placeholder="prospective.client@example.com" value={request.to} onChange={(e: any)=> {setRequest({...request,to: e.target.value});setFeedback(null);}} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input label="CC" inputMode="email" placeholder="Optional" value={request.cc} onChange={(e: any)=> setRequest({...request,cc: e.target.value})} />
                <Input label="BCC" inputMode="email" placeholder="Optional" value={request.bcc} onChange={(e: any)=> setRequest({...request,bcc: e.target.value})} />
              </div>
              <Input label="Subject" value={request.subject} onChange={(e: any)=> setRequest({...request,subject: e.target.value})} />
              <TextArea label="Message" rows={13} value={request.body} onChange={(e: any)=> setRequest({...request,body: e.target.value})} />

              {feedback && (
                <div className={cn('rounded-xl border px-3 py-2.5 text-xs font-semibold',feedback.type==='success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700')}>
                  {feedback.message}
                </div>
              )}
              <p className="text-[11px] leading-relaxed text-gray-400">Open Email uses your device's default email application. InvoiceFlow does not create a client record or send this request through the invoice email queue.</p>
            </div>

            <div className="grid grid-cols-1 gap-2 border-t border-gray-100 bg-gray-50 p-4 sm:flex sm:justify-end">
              <Button variant="secondary" onClick={copyRequest} className="w-full sm:w-auto"><SafeIcon icon={FiCopy} className="h-4 w-4" /> Copy Request</Button>
              <Button onClick={openEmail} className="w-full sm:w-auto"><SafeIcon icon={FiMail} className="h-4 w-4" /> Open Email</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};